import { ShowButton, TextField, useTable, EditButton, DeleteButton } from "@refinedev/antd";
import { useUpdate } from "@refinedev/core";
import { List, Table, Space, Popover, Form, Input, Typography, notification, Button, Spin, Switch, Tag, Select } from "antd";
import { format, formatDistanceToNow } from 'date-fns';
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import { FilterOutlined, ShareAltOutlined } from "@ant-design/icons";
import UserSearchSelect from '../../components/UserSearchSelect';
import { useState } from "react";

export function PlansList() {
  const { Title } = Typography;
  const navigate = useNavigate();
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);

  const { tableProps, searchFormProps } = useTable({
    syncWithLocation: true,
    queryOptions: {
      keepPreviousData: false,
      cacheTime: 20,
    },
    onSearch: (values: any) => {
      return [
        {
          field: "q",
          operator: "contains",
          value: values.q,
        },
        {
          field: "status",
          operator: "eq",
          value: values.status ? values.status.join(',') : undefined,
        },
        {
          field: "ownerID",
          operator: "eq",
          value: values.ownerID ? values.ownerID : undefined,
        },
      ]
    }
  });

  const handleSearchChange = debounce(() => {
    searchFormProps.form?.submit();
  }, 500);

  const { mutate: updateStatus, variables, isLoading: statusLoading } = useUpdate({
    resource: "subscription-plan",
    mutationMode: "optimistic",
    mutationOptions: {
      onSuccess: (data) => {
        notification.success({
          message: "Success",
          description: data?.data?.message,
        });
      },
      onError: (error) => {
        console.error("Error updating plan status:", error);
        notification.error({
          message: "Error",
          description: error?.response?.data?.message || "An error occurred while updating the plan status",
        });
      },
    }
  });

  const handleToggleStatus = (id: string, userID: string, currentStatus: string) => {
    const newStatus = 
      currentStatus === 'ACTIVE'
        ? 'RESTRICTED' : currentStatus === 'INACTIVE'
        ? 'INACTIVE' : 'ACTIVE'
      ;
    updateStatus({
      id,
      values: { status: newStatus, userID },
    });
  };

  const { mutate: updateFeatured, variables: featuredVariables, isLoading: featuredLoading } = useUpdate({
    resource: "subscription-plan",
    mutationMode: "optimistic",
    mutationOptions: {
      onSuccess: (data) => {
        notification.success({
          message: "Success",
          description: "Plan featured status updated successfully",
        });
      },
      onError: (error) => {
        notification.error({
          message: "Error",
          description: error?.response?.data?.message || "An error occurred while updating featured status",
        });
      },
    }
  });

  const handleToggleFeatured = (id: string, featured: boolean, userID: string) => {
    updateFeatured({
      id,
      values: { featured , userID},
    });
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
    },
    {
      title: "Price",
      dataIndex: "price",
      render: (value: number) => {
        if (value == null || value === 0) return "Free";
        return `$${value}`;
      },
    },
    {
      title: "Coach",
      dataIndex: "ownerName",
      render: (value: string, record: any) => {
        return (
          <TextField value={value ? value : "N/A"} 
            onClick={() => {
              navigate(`/users/${record.userID}`);
            }}
            style={{
              cursor: 'pointer',
              textDecoration: 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = 'underline';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = 'none';
            }}
          />
        );
      },
    },
    {
      title: 'Product Type',
      dataIndex: 'productType',
      render: (value: string) => {
        const colorMap: Record<string, string> = {
          COACH: 'blue',
          GYM: 'green',
          CLUB: 'purple',
        };
        return value ? <Tag color={colorMap[value] || 'default'}>{value}</Tag> : '-';
      },
    },
    {
      title: 'Categories',
      dataIndex: 'categories',
      render: (value: { id: string; title: string }[]) => {
        if (!value || value.length === 0) return '-';
        return (
          <Space wrap size={[0, 4]}>
            {value.map((cat) => (
              <Tag key={cat.id} color="cyan">{cat.title}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value: any, record: any) => (
        <div>
            <Popover content={format(value, "MM/dd/yy hh:mm a")}>
                <span>
                    {formatDistanceToNow(value)} ago
                </span>
            </Popover>
        </div>
    )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (value: string, record: any) => {
        return (
          <Space style={{
            color: value === 'ACTIVE'? "green" : value === 'RESTRICTED' ? "red" : "orange",
          }}>
            {value === 'ACTIVE' ? "Active" : value === 'RESTRICTED' ? "Restricted" : "Inactive"}
          </Space>
        );
      },
    },
    {
      title: 'Featured',
      dataIndex: 'featured',
      render: (value: boolean, record: any) => (
        <Switch
          checked={value}
          onChange={(checked: boolean) => handleToggleFeatured(record.id, checked, record.userID)}
          loading={featuredLoading && (featuredVariables && featuredVariables.id === record.id)}
        />
      ),
    },
    {
      title: 'Actions',
      render: (_: any, record: any) => {
        const shareUrl = `https://link.gymlive.app/find-detail-coach?productId=${record.id}`;
        const handleShare = async () => {
          try {
            await navigator.clipboard.writeText(shareUrl);
            notification.success({ message: "Link copied to clipboard", description: shareUrl });
          } catch {
            window.open(shareUrl, "_blank");
          }
        };
        return (
        <Space>
          <EditButton hideText size="small" recordItemId={record.id} />
          <DeleteButton hideText size="small" recordItemId={record.id} />
          <ShowButton hideText size="small" recordItemId={record.id} />
          <Button
            type="text"
            size="small"
            icon={<ShareAltOutlined />}
            onClick={handleShare}
            title="Copy share link"
            aria-label="Share"
          />
          <Button
            style={{
              width: 100,
              color: record.status === 'ACTIVE' ? "red" : record.status === 'RESTRICTED' ? "green" : "gray",
            }}
            onClick={() => handleToggleStatus(record.id, record?.userID, record.status)}
            disabled={record.status === 'INACTIVE'}
          >
            {record.status === 'ACTIVE'
              ? statusLoading && (variables && variables.id === record.id) ? <Spin /> : "Restrict"
              : statusLoading && (variables && variables.id === record.id) ? <Spin /> : "Activate"
            }
          </Button>
        </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: "4px", position: "relative" }}>
      <div style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 10,
        width: "100%",
        justifyContent: "flex-end",
        alignItems: "flex-start",
      }}>
        <Form {...searchFormProps} onValuesChange={handleSearchChange}>
          <Title level={4}>Filter and Search</Title>
          <div style={{ display: "flex", gap: 10 }}>
            <Popover
              placement="bottomRight"
              trigger="click"
              open={filterPopoverOpen}
              onOpenChange={setFilterPopoverOpen}
              content={
                <>
                  <Form.Item name="status" label="Status">
                    <Select
                      placeholder="Select status"
                      allowClear
                      style={{ width: 200 }}
                      mode="multiple"
                      getPopupContainer={(node) => node.parentElement ?? document.body}
                    >
                      <Select.Option value="ACTIVE">Active</Select.Option>
                      <Select.Option value="RESTRICTED">Restricted</Select.Option>
                      <Select.Option value="INACTIVE">Inactive</Select.Option>
                    </Select>
                  </Form.Item>
                  <Form.Item name="ownerID" label="Coach">
                    <UserSearchSelect
                      onChange={(value: any) => searchFormProps.form?.setFieldValue('ownerID', value)}
                      userType={[]}
                    />
                  </Form.Item>
                  <Form.Item wrapperCol={{ offset: 0 }}>
                    <Button
                      type="primary"
                      onClick={() => {
                        searchFormProps.form?.submit();
                        setFilterPopoverOpen(false);
                      }}
                    >
                      Apply filters
                    </Button>
                  </Form.Item>
                </>
              }
            >
              <Button icon={<FilterOutlined />}>Filters</Button>
            </Popover>
            <Form.Item name="q">
              <Input placeholder="Search by title or description" />
            </Form.Item>
          </div>
        </Form>
      </div>
      <Title level={3}>Subscription Plans</Title>
      <List>
        <Table
          {...tableProps}
          rowKey="id"
          columns={columns}
          pagination={{
            ...tableProps.pagination,
            showTotal: (total) => `Total ${total} items`,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
        />
      </List>
    </div>
  );
}
